import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ArcadeScoreBar from "./ArcadeScoreBar";
import { RotateCcw } from "lucide-react";

// ---------------------- Table geometry ----------------------
const W = 420;
const H = 640;
const BALL_R = 8;

// Physics tuning
const GRAVITY = 0.14;
const AIR_DAMP = 0.997;
const MAX_VX = 14;
const SUBSTEPS = 3;

// Plunger lane
const LANE_X = 380;                       // vertical wall separating main table from plunger lane
const LANE_GATE_Y = 90;                   // above this y, ball can cross freely (one-way top gate)
const PLUNGER_REST_Y = 590;               // ball rests here before launch
const PLUNGER_MAX = 18;                   // how far the plunger can be pulled back

// Flipper pivots — pivots are on the OUTER sides, tips reach toward center of drain
const FLIP_LEN = 62;
const FLIP_LEFT = { px: 130, py: 555 };
const FLIP_RIGHT = { px: 290, py: 555 };
const FLIP_REST_ANGLE = 0.45;             // radians below horizontal (tip pointing inward+down)
const FLIP_ACTIVE_ANGLE = -0.55;          // radians above horizontal (tip pointing inward+up)
const FLIP_ANG_SPEED = 0.35;              // radians per sub-step

// Wall segments {x1, y1, x2, y2, r=restitution}
const WALLS = [
  // Left outer wall
  { x1: 15, y1: 60,  x2: 15, y2: 500, r: 0.75 },
  // Right outer wall (below plunger lane join)
  { x1: LANE_X, y1: 60,  x2: LANE_X, y2: 500, r: 0.75 },
  // Plunger lane outer wall
  { x1: W - 15, y1: 30, x2: W - 15, y2: H, r: 0.5 },
  // Top arc — approximated with a fan of segments
  ...topArcSegments(),
  // Angled walls guiding to drain
  { x1: 15,  y1: 500, x2: FLIP_LEFT.px - 8,  y2: 545, r: 0.6 },
  { x1: LANE_X, y1: 500, x2: FLIP_RIGHT.px + 8, y2: 545, r: 0.6 },
  // Small slingshot triangles above each flipper
  { x1: 45,  y1: 470, x2: 100, y2: 500, r: 1.15 },   // left slingshot (bouncy)
  { x1: 100, y1: 500, x2: 45,  y2: 505, r: 1.0  },
  { x1: LANE_X - 30, y1: 470, x2: 280, y2: 500, r: 1.15 }, // right slingshot
  { x1: 280, y1: 500, x2: LANE_X - 30, y2: 505, r: 1.0 },
];

function topArcSegments() {
  const cx = (15 + LANE_X) / 2;
  const cy = 90;
  const rad = (LANE_X - 15) / 2;
  const segs = [];
  const N = 14;
  for (let i = 0; i < N; i++) {
    const a1 = Math.PI + (i / N) * Math.PI;
    const a2 = Math.PI + ((i + 1) / N) * Math.PI;
    segs.push({
      x1: cx + Math.cos(a1) * rad, y1: cy + Math.sin(a1) * rad,
      x2: cx + Math.cos(a2) * rad, y2: cy + Math.sin(a2) * rad,
      r: 0.7,
    });
  }
  return segs;
}

// Bumpers — round pop-bumpers on the upper playfield
const BUMPERS = [
  { x: 130, y: 180, r: 22, color: "#FF3366", points: 100 },
  { x: 270, y: 180, r: 22, color: "#00E5FF", points: 100 },
  { x: 200, y: 260, r: 26, color: "#CCFF00", points: 150 },
  { x:  85, y: 340, r: 18, color: "#F59E0B", points: 75 },
  { x: 315, y: 340, r: 18, color: "#F59E0B", points: 75 },
];

// ---------------------- Component ----------------------
export default function Pinball() {
  const { user } = useAuth();
  const canvasRef = useRef(null);

  const [score, setScore] = useState(0);
  const [balls, setBalls] = useState(3);
  const [combo, setCombo] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  // Physics state — mutable refs to avoid React re-renders per frame
  const ballRef      = useRef(null);      // {x, y, vx, vy, live}
  const flipLRef     = useRef(FLIP_REST_ANGLE);
  const flipRRef     = useRef(FLIP_REST_ANGLE);
  const flipLDownRef = useRef(false);
  const flipRDownRef = useRef(false);
  const plungerRef   = useRef(0);         // 0..PLUNGER_MAX charge distance
  const plungerHeldRef = useRef(false);
  const bumperHitRef = useRef({});        // for pop-animation timing
  const comboExpiryRef = useRef(0);

  const runningRef = useRef(false);
  const scoreRef   = useRef(0);
  const ballsRef   = useRef(3);
  const comboRef   = useRef(0);
  const goRef      = useRef(false);

  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { ballsRef.current = balls; }, [balls]);
  useEffect(() => { comboRef.current = combo; }, [combo]);
  useEffect(() => { goRef.current = gameOver; }, [gameOver]);

  const seedBall = useCallback(() => {
    ballRef.current = { x: W - 30, y: PLUNGER_REST_Y, vx: 0, vy: 0, live: true, launched: false };
  }, []);

  const startGame = () => {
    setScore(0);
    setBalls(3);
    setCombo(0);
    setGameOver(false);
    setSubmitted(false);
    seedBall();
    runningRef.current = true;
  };

  // Auto-init one ball so canvas shows something ready to play
  useEffect(() => { seedBall(); runningRef.current = true; }, [seedBall]);

  // Submit high score once when game ends
  useEffect(() => {
    if (gameOver && !submitted && score > 0 && user) {
      setSubmitted(true);
      api.post("/arcade/score", { game_slug: "pinball", score })
        .then(r => {
          if (r.data.new_best) toast.success(`New personal best: ${score.toLocaleString()}!`);
          setRefreshKey(k => k + 1);
        })
        .catch(() => {});
    }
  }, [gameOver, submitted, score, user]);

  // Keyboard input
  useEffect(() => {
    const down = (e) => {
      if (e.repeat) return;
      if (e.key === "z" || e.key === "Z" || e.key === "ArrowLeft") { flipLDownRef.current = true; e.preventDefault(); }
      else if (e.key === "/" || e.key === "m" || e.key === "M" || e.key === "ArrowRight") { flipRDownRef.current = true; e.preventDefault(); }
      else if (e.key === " ") { plungerHeldRef.current = true; e.preventDefault(); }
    };
    const up = (e) => {
      if (e.key === "z" || e.key === "Z" || e.key === "ArrowLeft") flipLDownRef.current = false;
      else if (e.key === "/" || e.key === "m" || e.key === "M" || e.key === "ArrowRight") flipRDownRef.current = false;
      else if (e.key === " ") {
        // Release plunger — launch if ball is in the lane and not yet launched
        const b = ballRef.current;
        if (b && !b.launched && b.x > LANE_X + 5) {
          const power = 6 + (plungerRef.current / PLUNGER_MAX) * 14;
          b.vy = -power;
          b.vx = -0.5 - Math.random() * 0.5;
          b.launched = true;
        }
        plungerRef.current = 0;
        plungerHeldRef.current = false;
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  // ---------------------- Physics + render loop ----------------------
  useEffect(() => {
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;

      if (runningRef.current && !goRef.current) {
        // Plunger charge while held
        if (plungerHeldRef.current && plungerRef.current < PLUNGER_MAX) {
          plungerRef.current = Math.min(PLUNGER_MAX, plungerRef.current + 0.8);
        }
        // Flipper angular motion
        flipLRef.current = flipLDownRef.current
          ? Math.max(FLIP_ACTIVE_ANGLE, flipLRef.current - FLIP_ANG_SPEED)
          : Math.min(FLIP_REST_ANGLE, flipLRef.current + FLIP_ANG_SPEED * 0.65);
        flipRRef.current = flipRDownRef.current
          ? Math.max(FLIP_ACTIVE_ANGLE, flipRRef.current - FLIP_ANG_SPEED)
          : Math.min(FLIP_REST_ANGLE, flipRRef.current + FLIP_ANG_SPEED * 0.65);

        // Substep physics
        for (let s = 0; s < SUBSTEPS; s++) stepBall();
      }

      // Combo timeout
      if (comboRef.current > 0 && performance.now() > comboExpiryRef.current) {
        setCombo(0);
      }

      render(ctx);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------- Ball step ----------------------
  const addPoints = (n) => {
    const now = performance.now();
    let c = comboRef.current;
    if (now < comboExpiryRef.current) c += 1; else c = 1;
    comboExpiryRef.current = now + 1400;
    const mult = Math.min(5, c);
    setCombo(c);
    setScore(s => s + n * mult);
  };

  function stepBall() {
    const b = ballRef.current;
    if (!b || !b.live) return;

    // Pre-launch: pin the ball on the plunger, no gravity/drain
    if (!b.launched) {
      b.x = W - 22 + 7;
      b.y = PLUNGER_REST_Y - plungerRef.current;   // ball rides the plunger head
      b.vx = 0; b.vy = 0;
      return;
    }

    b.vy += GRAVITY;
    b.vx *= AIR_DAMP; b.vy *= AIR_DAMP;
    if (b.vx > MAX_VX) b.vx = MAX_VX;
    if (b.vx < -MAX_VX) b.vx = -MAX_VX;
    if (b.vy > MAX_VX) b.vy = MAX_VX;
    if (b.vy < -MAX_VX) b.vy = -MAX_VX;

    b.x += b.vx / SUBSTEPS;
    b.y += b.vy / SUBSTEPS;

    // Ceiling
    if (b.y < BALL_R + 15) { b.y = BALL_R + 15; b.vy = -b.vy * 0.6; }

    // Once launched and past the gate, don't let the ball re-enter the plunger lane
    if (b.launched && b.y > LANE_GATE_Y + 15 && b.x > LANE_X - BALL_R && b.x < LANE_X + BALL_R + 8) {
      b.x = LANE_X - BALL_R;
      b.vx = -Math.abs(b.vx) * 0.6;
    }
    // Plunger lane right wall
    if (b.x > W - 15 - BALL_R) { b.x = W - 15 - BALL_R; b.vx = -Math.abs(b.vx) * 0.6; }

    // Walls
    for (const w of WALLS) collideSegment(b, w.x1, w.y1, w.x2, w.y2, w.r);

    // Bumpers
    for (const bp of BUMPERS) {
      const dx = b.x - bp.x, dy = b.y - bp.y;
      const d = Math.hypot(dx, dy);
      if (d < BALL_R + bp.r) {
        const nx = dx / (d || 1), ny = dy / (d || 1);
        const dot = b.vx * nx + b.vy * ny;
        b.vx = (b.vx - 2 * dot * nx) + nx * 3.2;
        b.vy = (b.vy - 2 * dot * ny) + ny * 3.2;
        b.x = bp.x + nx * (BALL_R + bp.r + 0.5);
        b.y = bp.y + ny * (BALL_R + bp.r + 0.5);
        addPoints(bp.points);
        bumperHitRef.current[bp.x + "," + bp.y] = performance.now();
      }
    }

    // Flippers as rotating line segments
    collideFlipper(b, FLIP_LEFT, flipLRef.current, -1);
    collideFlipper(b, FLIP_RIGHT, flipRRef.current,  1);

    // Drain
    if (b.y > H + 20) {
      b.live = false;
      const nb = ballsRef.current - 1;
      setBalls(nb);
      setCombo(0);
      if (nb <= 0) {
        setGameOver(true);
      } else {
        seedBall();
      }
    }
  }

  function collideSegment(b, x1, y1, x2, y2, restitution) {
    const abx = x2 - x1, aby = y2 - y1;
    const apx = b.x - x1, apy = b.y - y1;
    const len2 = abx * abx + aby * aby || 1;
    let t = (apx * abx + apy * aby) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = x1 + abx * t, cy = y1 + aby * t;
    const dx = b.x - cx, dy = b.y - cy;
    const d = Math.hypot(dx, dy);
    if (d < BALL_R) {
      const nx = dx / (d || 1), ny = dy / (d || 1);
      const dot = b.vx * nx + b.vy * ny;
      if (dot < 0) {
        b.vx = (b.vx - 2 * dot * nx) * restitution;
        b.vy = (b.vy - 2 * dot * ny) * restitution;
      }
      b.x = cx + nx * (BALL_R + 0.5);
      b.y = cy + ny * (BALL_R + 0.5);
    }
  }

  function collideFlipper(b, pivot, angle, side) {
    // Left flipper (side=-1): tip at pivot + FLIP_LEN * (cos(angle), sin(angle)) — extends RIGHT toward center
    // Right flipper (side=+1): tip at pivot + FLIP_LEN * (-cos(angle), sin(angle)) — extends LEFT toward center
    const tipX = pivot.px + Math.cos(angle) * FLIP_LEN * (side === -1 ? 1 : -1);
    const tipY = pivot.py + Math.sin(angle) * FLIP_LEN;
    const before = { vx: b.vx, vy: b.vy };
    collideSegment(b, pivot.px, pivot.py, tipX, tipY, 1.0);
    // If flipper is actively rotating up and we hit it, add extra impulse
    const moving = (side === -1 && flipLDownRef.current && flipLRef.current > FLIP_ACTIVE_ANGLE + 0.05) ||
                   (side ===  1 && flipRDownRef.current && flipRRef.current > FLIP_ACTIVE_ANGLE + 0.05);
    if (moving && (b.vy !== before.vy || b.vx !== before.vx)) {
      b.vy -= 4.5;
      b.vx += side === -1 ? 2.2 : -2.2;
      if (Math.abs(b.vx) > MAX_VX) b.vx = Math.sign(b.vx) * MAX_VX;
      if (Math.abs(b.vy) > MAX_VX) b.vy = Math.sign(b.vy) * MAX_VX;
      addPoints(2);
    }
  }

  // ---------------------- Render ----------------------
  function render(ctx) {
    // Background gradient
    const grd = ctx.createLinearGradient(0, 0, 0, H);
    grd.addColorStop(0, "#0a0a15");
    grd.addColorStop(1, "#050510");
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);

    // Grid dots
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    for (let y = 20; y < H; y += 24) {
      for (let x = 20; x < W; x += 24) {
        ctx.fillRect(x, y, 1, 1);
      }
    }

    // Playfield walls
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#374151";
    for (const w of WALLS) {
      ctx.beginPath();
      ctx.moveTo(w.x1, w.y1);
      ctx.lineTo(w.x2, w.y2);
      ctx.stroke();
    }
    // Slingshot fill (bright red-cyan)
    ctx.fillStyle = "rgba(255,51,102,0.15)";
    ctx.beginPath(); ctx.moveTo(45, 470); ctx.lineTo(100, 500); ctx.lineTo(45, 505); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "rgba(0,229,255,0.15)";
    ctx.beginPath(); ctx.moveTo(LANE_X - 30, 470); ctx.lineTo(280, 500); ctx.lineTo(LANE_X - 30, 505); ctx.closePath(); ctx.fill();

    // Plunger lane divider
    ctx.strokeStyle = "#2a2a3a";
    ctx.beginPath(); ctx.moveTo(LANE_X, LANE_GATE_Y); ctx.lineTo(LANE_X, H); ctx.stroke();

    // Bumpers
    const now = performance.now();
    for (const bp of BUMPERS) {
      const hit = bumperHitRef.current[bp.x + "," + bp.y] || 0;
      const glow = Math.max(0, 1 - (now - hit) / 180);
      ctx.shadowBlur = 14 + glow * 20;
      ctx.shadowColor = bp.color;
      ctx.fillStyle = bp.color;
      ctx.beginPath();
      ctx.arc(bp.x, bp.y, bp.r + glow * 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#050510";
      ctx.beginPath(); ctx.arc(bp.x, bp.y, bp.r - 6, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = bp.color;
      ctx.font = "bold 10px monospace";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(bp.points, bp.x, bp.y);
    }

    // Flippers
    drawFlipper(ctx, FLIP_LEFT, flipLRef.current, -1);
    drawFlipper(ctx, FLIP_RIGHT, flipRRef.current, 1);

    // Plunger
    ctx.fillStyle = "#00E5FF";
    ctx.shadowBlur = 8; ctx.shadowColor = "#00E5FF";
    ctx.fillRect(W - 22, PLUNGER_REST_Y + 8 + plungerRef.current, 14, 40);
    ctx.shadowBlur = 0;

    // Ball
    const b = ballRef.current;
    if (b) {
      ctx.shadowBlur = 20; ctx.shadowColor = "#ffffff";
      const grad = ctx.createRadialGradient(b.x - 2, b.y - 3, 1, b.x, b.y, BALL_R);
      grad.addColorStop(0, "#ffffff");
      grad.addColorStop(1, "#c0c8d0");
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(b.x, b.y, BALL_R, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;
    }

    // If pre-launch, hint
    if (b && !b.launched) {
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "10px monospace"; ctx.textAlign = "center";
      ctx.fillText("HOLD SPACE", W - 22 + 7, PLUNGER_REST_Y - 10);
      ctx.fillText("TO LAUNCH", W - 22 + 7, PLUNGER_REST_Y);
    }
  }

  function drawFlipper(ctx, pivot, angle, side) {
    ctx.save();
    ctx.translate(pivot.px, pivot.py);
    if (side === 1) ctx.scale(-1, 1);   // mirror for right flipper so tip extends toward center
    ctx.rotate(angle);
    ctx.shadowBlur = 12; ctx.shadowColor = "#CCFF00";
    const g = ctx.createLinearGradient(0, 0, FLIP_LEN, 0);
    g.addColorStop(0, "#a3e635");
    g.addColorStop(1, "#CCFF00");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(0, -7);
    ctx.lineTo(FLIP_LEN - 4, -4);
    ctx.arc(FLIP_LEN - 4, 0, 4, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(0, 7);
    ctx.arc(0, 0, 7, Math.PI / 2, -Math.PI / 2);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
    // Pivot cap
    ctx.fillStyle = "#333";
    ctx.beginPath(); ctx.arc(0, 0, 3, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  const comboText = combo >= 2 ? `×${Math.min(5, combo)} combo!` : "";

  return (
    <div className="sc-card p-5" data-testid="pinball-game">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Neon Pinball</div>
          <h2 className="sc-heading text-2xl font-black">Keep the ball alive. Chain the bumpers.</h2>
        </div>
        <div className="flex items-center gap-4 sc-mono text-sm">
          <div>SCORE <span className="text-[color:var(--sc-volt)] font-bold" data-testid="pinball-score">{score.toLocaleString()}</span></div>
          <div>BALLS <span className="text-[color:var(--sc-pink)] font-bold" data-testid="pinball-balls">{"●".repeat(balls) || "—"}</span></div>
          {comboText && <div className="text-[color:var(--sc-cyan)] font-bold anim-pulse">{comboText}</div>}
        </div>
      </div>
      <div className="flex justify-center">
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="rounded-sm border border-white/10 shadow-[0_0_30px_rgba(255,51,102,0.25)]"
          data-testid="pinball-canvas"
        />
      </div>
      <div className="text-[11px] text-zinc-500 text-center mt-3">
        <span className="text-white">HOLD SPACE</span> to charge plunger, release to launch · <span className="text-white">Z / ←</span> left flipper · <span className="text-white">/ M / →</span> right flipper · Chain bumpers for combo multiplier
      </div>
      {gameOver && (
        <div className="mt-4 text-center">
          <div className="sc-heading text-3xl font-black text-[color:var(--sc-pink)]">Game Over</div>
          <div className="text-sm text-zinc-400 mb-3">Final score: <span className="text-[color:var(--sc-volt)] font-bold">{score.toLocaleString()}</span></div>
          <button onClick={startGame} className="sc-btn mx-auto flex items-center gap-2" data-testid="pinball-restart">
            <RotateCcw className="w-4 h-4" /> Play Again
          </button>
        </div>
      )}
      <ArcadeScoreBar gameSlug="pinball" refreshKey={refreshKey} />
    </div>
  );
}
